import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tabs, Tag, Button, Input, Select, Popconfirm, Alert,
  Row, Col, Statistic, Space, Typography, Empty, Modal, theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CalendarOutlined, DashboardOutlined, FileDoneOutlined, PlusOutlined,
  DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined,
  CheckCircleOutlined, ClockCircleOutlined, DollarOutlined, FlagOutlined,
  LeftOutlined, RightOutlined, PlayCircleOutlined, FileAddOutlined,
  UnorderedListOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { PMSchedule, WorkOrder, OrgPMTask, FacilityAssessment } from '../../models/types';
import {
  createPMSchedule, getPMSchedules, updatePMSchedule, deletePMSchedule,
  createWorkOrder, getWorkOrders, updateWorkOrder, deleteWorkOrder,
} from '../../firebase/pmScheduler';
import { getFacilityAssessments } from '../../firebase/firestore';
import { buildSeedRows, SeedRow } from './pmRuleTemplates';
import { getOrgTaskLibrary } from '../../firebase/pmTaskLibrary';
import { PM_TASK_LIBRARY, getTradeRate } from './pmReferenceData';
import { useOrg } from '../../context/OrgContext';

interface Props { projectId: string; }

type Tab = 'dashboard' | 'schedules' | 'workorders' | 'calendar';

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Parse date-only strings safely in local time (avoids UTC midnight → prev day issue)
function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
}

function addFrequency(from: string, value: number, unit: PMSchedule['frequencyUnit']): string {
  const d = parseLocalDate(from);
  if (unit === 'days') d.setDate(d.getDate() + value);
  else if (unit === 'weeks') d.setDate(d.getDate() + value * 7);
  else if (unit === 'months') d.setMonth(d.getMonth() + value);
  else if (unit === 'years') d.setFullYear(d.getFullYear() + value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isOverdue(nextDue: string): boolean { return nextDue < today(); }
function isDueSoon(nextDue: string): boolean {
  const t = today();
  const soon = parseLocalDate(t);
  soon.setDate(soon.getDate() + 7);
  const soonStr = `${soon.getFullYear()}-${String(soon.getMonth() + 1).padStart(2, '0')}-${String(soon.getDate()).padStart(2, '0')}`;
  return nextDue >= t && nextDue <= soonStr;
}

const MONTH_NAMES = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];
const DOW_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function buildCalendarGrid(year: number, month: number): (string | null)[] {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const grid: (string | null)[] = [];
  for (let i = 0; i < firstDay; i++) grid.push(null);
  for (let d = 1; d <= daysInMonth; d++)
    grid.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  while (grid.length % 7 !== 0) grid.push(null);
  return grid;
}

/* AntD preset tag color per schedule urgency (was bootstrap danger/warning/success) */
function scheduleChipColor(s: PMSchedule): string {
  if (isOverdue(s.nextDueDate)) return 'red';
  if (isDueSoon(s.nextDueDate)) return 'gold';
  return 'green';
}

const PRIORITY_COLORS: Record<WorkOrder['priority'], string> = {
  P1: 'red', P2: 'gold', P3: 'blue', P4: 'default',
};
const PRIORITY_LABELS: Record<WorkOrder['priority'], string> = {
  P1: 'P1 — Emergency', P2: 'P2 — Urgent', P3: 'P3 — Routine', P4: 'P4 — Deferred',
};
const STATUS_COLORS: Record<WorkOrder['status'], string> = {
  open: 'blue', 'in-progress': 'gold', completed: 'green',
  deferred: 'default', cancelled: 'default',
};
const SCHEDULE_STATUS_COLORS: Record<string, string> = {
  active: 'green', deferred: 'red', inactive: 'default',
};

const emptyScheduleForm = () => ({
  assetDescription: '', assetLocation: '', taskId: '',
  taskName: '', trade: '', frequencyValue: 1,
  frequencyUnit: 'months' as PMSchedule['frequencyUnit'],
  estimatedHours: 1, complianceRequired: false, complianceStandard: '',
  nextDueDate: today(), status: 'active' as PMSchedule['status'], notes: '',
});

const emptyWOForm = () => ({
  title: '', description: '', assetDescription: '', assetLocation: '',
  trade: '', priority: 'P3' as WorkOrder['priority'], dueDate: '',
  assignedTo: '', notes: '',
});

const PMSchedulerModule: React.FC<Props> = ({ projectId }) => {
  const { token } = theme.useToken();
  const { orgId } = useOrg();
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [schedules, setSchedules] = useState<PMSchedule[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [orgTasks, setOrgTasks] = useState<OrgPMTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Schedule form state
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleForm, setScheduleForm] = useState(emptyScheduleForm());
  const [scheduleFormError, setScheduleFormError] = useState<string | null>(null);
  const [savingSchedule, setSavingSchedule] = useState(false);

  // WO form state
  const [showWOForm, setShowWOForm] = useState(false);
  const [woForm, setWOForm] = useState(emptyWOForm());
  const [woFormError, setWOFormError] = useState<string | null>(null);
  const [savingWO, setSavingWO] = useState(false);

  // WO complete state
  const [completingWO, setCompletingWO] = useState<string | null>(null);
  const [completeForm, setCompleteForm] = useState({ completedDate: today(), resolution: '', laborHours: '' });

  // Calendar state
  const [calendarDate, setCalendarDate] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // Seed-from-assessment state
  const [showSeed, setShowSeed] = useState(false);
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedAssessments, setSeedAssessments] = useState<FacilityAssessment[]>([]);
  const [seedAssessmentId, setSeedAssessmentId] = useState<string | null>(null);
  const [seedRows, setSeedRows] = useState<SeedRow[]>([]);
  const [seedUnmatched, setSeedUnmatched] = useState<string[]>([]);
  const [seedSelected, setSeedSelected] = useState<React.Key[]>([]);

  // Filters
  const [scheduleFilter, setScheduleFilter] = useState<'all' | 'overdue' | 'due-soon'>('all');
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  const [editScheduleForm, setEditScheduleForm] = useState<any>({});
  const [editingWOId, setEditingWOId] = useState<string | null>(null);
  const [editWOForm, setEditWOForm] = useState<any>({});
  const [woStatusFilter, setWOStatusFilter] = useState<string>('open');
  const [woTypeFilter, setWOTypeFilter] = useState<string>('all');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [s, w, tasks] = await Promise.all([
        getPMSchedules(projectId),
        getWorkOrders(projectId),
        orgId ? getOrgTaskLibrary(orgId) : Promise.resolve([] as OrgPMTask[]),
      ]);
      setSchedules(s); setWorkOrders(w); setOrgTasks(tasks);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [projectId, orgId]);

  useEffect(() => { load(); }, [load]);

  // ── Seed from facility assessment ──────────────────────────────────────

  const selectSeedAssessment = useCallback((assessment: FacilityAssessment) => {
    setSeedAssessmentId(assessment.id ?? null);
    const source = orgId && orgTasks.length ? orgTasks : PM_TASK_LIBRARY;
    const { rows, unmatched } = buildSeedRows(assessment, source, schedules);
    setSeedRows(rows);
    setSeedUnmatched(unmatched);
    setSeedSelected(rows.filter(r => !r.exists).map(r => r.key));
  }, [orgId, orgTasks, schedules]);

  const openSeed = async () => {
    setSeedBusy(true); setError(null);
    try {
      const list = await getFacilityAssessments(projectId);
      if (!list.length) {
        setError('No facility assessments in this project yet — create one in the Facility module first.');
        return;
      }
      setSeedAssessments(list);
      selectSeedAssessment(list[0]);
      setShowSeed(true);
    } catch (e: any) { setError(e.message); }
    finally { setSeedBusy(false); }
  };

  const confirmSeed = async () => {
    const chosen = seedRows.filter(r => seedSelected.includes(r.key));
    if (!chosen.length) { setShowSeed(false); return; }
    setSeedBusy(true);
    try {
      const building = seedAssessments.find(a => a.id === seedAssessmentId)?.buildingName ?? '';
      await Promise.all(chosen.map(r => createPMSchedule({
        projectId,
        assetDescription: r.assetDescription,
        assetLocation: r.assetLocation,
        taskName: r.taskName,
        trade: r.trade,
        frequencyValue: r.frequencyValue,
        frequencyUnit: r.frequencyUnit,
        estimatedHours: r.estimatedHours,
        complianceRequired: r.complianceRequired,
        ...(r.complianceStandard ? { complianceStandard: r.complianceStandard } : {}),
        nextDueDate: addFrequency(today(), r.frequencyValue, r.frequencyUnit),
        status: 'active',
        notes: `Seeded from facility assessment${building ? ` "${building}"` : ''}${r.rating ? ` — condition ${r.rating}` : ''}`,
      })));
      setShowSeed(false);
      await load();
      setActiveTab('schedules');
    } catch (e: any) { setError(e.message); }
    finally { setSeedBusy(false); }
  };

  // ── Schedule handlers ──────────────────────────────────────────────────

  const handleTaskSelect = (taskId: string) => {
    const source = orgId ? orgTasks : PM_TASK_LIBRARY;
    const task = source.find(t => t.id === taskId);
    if (task) {
      setScheduleForm(f => ({
        ...f, taskId, taskName: task.taskName, trade: task.trade,
        frequencyValue: task.frequencyValue, frequencyUnit: task.frequencyUnit,
        estimatedHours: task.estimatedHours, complianceRequired: task.complianceRequired,
        complianceStandard: task.complianceStandard ?? '',
      }));
    } else {
      setScheduleForm(f => ({ ...f, taskId: '' }));
    }
  };

  const saveSchedule = async () => {
    const missing: string[] = [];
    if (!scheduleForm.assetDescription) missing.push('Asset / Equipment Description');
    if (!scheduleForm.taskName) missing.push('Task Name');
    if (!scheduleForm.nextDueDate) missing.push('Next Due Date');
    if (missing.length > 0) {
      setScheduleFormError(`Please fill in the required field(s): ${missing.join(', ')}.`);
      return;
    }
    setScheduleFormError(null);
    setSavingSchedule(true);
    try {
      await createPMSchedule({
        projectId,
        assetDescription: scheduleForm.assetDescription,
        assetLocation: scheduleForm.assetLocation,
        taskName: scheduleForm.taskName,
        trade: scheduleForm.trade,
        frequencyValue: scheduleForm.frequencyValue,
        frequencyUnit: scheduleForm.frequencyUnit,
        estimatedHours: scheduleForm.estimatedHours,
        complianceRequired: scheduleForm.complianceRequired,
        ...(scheduleForm.complianceStandard ? { complianceStandard: scheduleForm.complianceStandard } : {}),
        nextDueDate: scheduleForm.nextDueDate,
        status: scheduleForm.status,
        ...(scheduleForm.notes ? { notes: scheduleForm.notes } : {}),
      });
      setShowScheduleForm(false);
      setScheduleForm(emptyScheduleForm());
      await load();
    } catch (e: any) { setError(e.message); }
    finally { setSavingSchedule(false); }
  };

  const completeSchedule = async (s: PMSchedule) => {
    const lastCompletedDate = today();
    const nextDueDate = addFrequency(lastCompletedDate, s.frequencyValue, s.frequencyUnit);
    await updatePMSchedule(s.id, { lastCompletedDate, nextDueDate });
    await load();
  };

  const createWOFromSchedule = async (s: PMSchedule) => {
    await createWorkOrder({
      projectId,
      scheduleId: s.id,
      assetDescription: s.assetDescription,
      assetLocation: s.assetLocation,
      type: 'PM',
      priority: isOverdue(s.nextDueDate) ? 'P2' : 'P3',
      status: 'open',
      title: `${s.taskName} — ${s.assetDescription}`,
      description: `PM task due ${s.nextDueDate}. Trade: ${s.trade}. Est. ${s.estimatedHours}h.${s.complianceRequired && s.complianceStandard ? ` Compliance: ${s.complianceStandard}.` : ''}`,
      trade: s.trade,
      dueDate: s.nextDueDate,
    });
    await load();
    setActiveTab('workorders');
  };

  const deleteSchedule = async (id: string) => {
    await deletePMSchedule(id);
    await load();
  };

  const startEditSchedule = (s: PMSchedule) => {
    setEditingScheduleId(s.id!);
    setEditScheduleForm({
      assetDescription: s.assetDescription, assetLocation: s.assetLocation ?? '',
      taskName: s.taskName, trade: s.trade,
      frequencyValue: s.frequencyValue, frequencyUnit: s.frequencyUnit,
      estimatedHours: s.estimatedHours, nextDueDate: s.nextDueDate,
      notes: s.notes ?? '', status: s.status,
    });
  };

  const saveEditSchedule = async (id: string) => {
    await updatePMSchedule(id, editScheduleForm);
    setEditingScheduleId(null);
    await load();
  };

  // ── Work Order handlers ────────────────────────────────────────────────

  const saveWO = async () => {
    const missing: string[] = [];
    if (!woForm.title) missing.push('Title');
    if (!woForm.trade) missing.push('Trade');
    if (missing.length > 0) {
      setWOFormError(`Please fill in: ${missing.join(', ')}.`);
      return;
    }
    setWOFormError(null);
    setSavingWO(true);
    try {
      await createWorkOrder({
        projectId, type: 'reactive',
        title: woForm.title, description: woForm.description,
        assetDescription: woForm.assetDescription || undefined,
        assetLocation: woForm.assetLocation || undefined,
        trade: woForm.trade, priority: woForm.priority,
        status: 'open', dueDate: woForm.dueDate || undefined,
        assignedTo: woForm.assignedTo || undefined,
        notes: woForm.notes || undefined,
      });
      setShowWOForm(false);
      setWOForm(emptyWOForm());
      setWOFormError(null);
      await load();
    } catch (e: any) { setError(e.message); }
    finally { setSavingWO(false); }
  };

  const submitComplete = async (id: string) => {
    await updateWorkOrder(id, {
      status: 'completed',
      completedDate: completeForm.completedDate,
      resolution: completeForm.resolution || undefined,
      laborHours: completeForm.laborHours ? parseFloat(completeForm.laborHours) : undefined,
    });
    setCompletingWO(null);
    setCompleteForm({ completedDate: today(), resolution: '', laborHours: '' });
    await load();
  };

  const deleteWO = async (id: string) => {
    await deleteWorkOrder(id);
    await load();
  };

  const startEditWO = (wo: WorkOrder) => {
    setEditingWOId(wo.id!);
    setEditWOForm({ title: wo.title, description: wo.description ?? '', priority: wo.priority, trade: wo.trade, dueDate: wo.dueDate ?? '', assignedTo: wo.assignedTo ?? '', status: wo.status });
  };

  const saveEditWO = async (id: string) => {
    await updateWorkOrder(id, editWOForm);
    setEditingWOId(null);
    await load();
  };

  // ── Computed ───────────────────────────────────────────────────────────

  const overdue = schedules.filter(s => s.status === 'active' && isOverdue(s.nextDueDate));
  const dueSoon = schedules.filter(s => s.status === 'active' && isDueSoon(s.nextDueDate));
  const openWOs = workOrders.filter(w => w.status === 'open' || w.status === 'in-progress');
  const filteredWOs = workOrders.filter(w => {
    if (woStatusFilter !== 'all' && w.status !== woStatusFilter) return false;
    if (woTypeFilter !== 'all' && w.type !== woTypeFilter) return false;
    return true;
  });

  const filteredSchedules = schedules.filter(s => {
    if (scheduleFilter === 'overdue') return s.status === 'active' && isOverdue(s.nextDueDate);
    if (scheduleFilter === 'due-soon') return s.status === 'active' && isDueSoon(s.nextDueDate);
    return true;
  });

  // ── Cost Estimates ─────────────────────────────────────────────────────

  // Annual occurrences per schedule
  function annualOccurrences(s: { frequencyValue: number; frequencyUnit: PMSchedule['frequencyUnit'] }): number {
    const map = { days: 365, weeks: 52, months: 12, years: 1 };
    return map[s.frequencyUnit] / s.frequencyValue;
  }

  const activeSchedules = schedules.filter(s => s.status === 'active');

  // Annual cost per schedule
  const scheduleCosts = activeSchedules.map(s => ({
    id: s.id,
    taskName: s.taskName,
    trade: s.trade,
    assetDescription: s.assetDescription,
    hoursPerOccurrence: s.estimatedHours,
    occurrencesPerYear: annualOccurrences(s),
    ratePerHour: getTradeRate(s.trade),
    annualCost: s.estimatedHours * annualOccurrences(s) * getTradeRate(s.trade),
  }));

  const totalAnnualPMCost = scheduleCosts.reduce((sum, c) => sum + c.annualCost, 0);
  const totalMonthlyPMCost = totalAnnualPMCost / 12;

  // Cost by trade
  const costByTrade = scheduleCosts.reduce((acc, c) => {
    acc[c.trade] = (acc[c.trade] ?? 0) + c.annualCost;
    return acc;
  }, {} as Record<string, number>);
  const topTrades = Object.entries(costByTrade)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  const navigateTo = (tab: Tab, schedFilter?: typeof scheduleFilter, woFilter?: string) => {
    setActiveTab(tab);
    setSelectedDay(null);
    if (schedFilter !== undefined) setScheduleFilter(schedFilter);
    if (woFilter !== undefined) setWOStatusFilter(woFilter);
  };

  const calendarGrid = buildCalendarGrid(calendarDate.getFullYear(), calendarDate.getMonth());
  const schedulesByDay = activeSchedules.reduce((acc, s) => {
    if (!acc[s.nextDueDate]) acc[s.nextDueDate] = [];
    acc[s.nextDueDate].push(s);
    return acc;
  }, {} as Record<string, PMSchedule[]>);
  const selectedDaySchedules = selectedDay ? (schedulesByDay[selectedDay] ?? []) : [];

  type TaskItem = { id: string; category: string; assetType: string; taskName: string };
  const taskSource: TaskItem[] = orgId ? orgTasks : PM_TASK_LIBRARY;
  const taskCategories = Array.from(new Set(taskSource.map(t => t.category))).sort();
  const groupedTasks = taskCategories.reduce((acc, cat) => {
    const byType: Record<string, TaskItem[]> = {};
    taskSource.filter(t => t.category === cat).forEach(t => {
      if (!byType[t.assetType]) byType[t.assetType] = [];
      byType[t.assetType].push(t);
    });
    acc[cat] = byType;
    return acc;
  }, {} as Record<string, Record<string, TaskItem[]>>);

  if (loading) return <Card loading />;

  const formGrid: React.CSSProperties = { display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' };
  const rowTint = (s: PMSchedule): React.CSSProperties =>
    s.status === 'active' && isOverdue(s.nextDueDate) ? { background: token.colorErrorBg }
    : s.status === 'active' && isDueSoon(s.nextDueDate) ? { background: token.colorWarningBg } : {};

  const field = (label: string, node: React.ReactNode, span?: boolean) => (
    <div style={span ? { gridColumn: '1 / -1' } : undefined}>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{label}</div>
      {node}
    </div>
  );

  const freqUnitOptions = [
    { value: 'days', label: 'Days' }, { value: 'weeks', label: 'Weeks' },
    { value: 'months', label: 'Months' }, { value: 'years', label: 'Years' },
  ];

  const taskLibraryOptions = Object.entries(groupedTasks).flatMap(([cat, byType]) =>
    Object.entries(byType).map(([assetType, tasks]) => ({
      label: `${cat} — ${assetType}`,
      options: tasks.map(t => ({ value: t.id, label: t.taskName })),
    })));

  // ── Renderers ──────────────────────────────────────────────────────────

  const renderDashboard = () => (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {[
          { label: 'Total Schedules', value: schedules.filter(s => s.status === 'active').length, color: token.colorText, onClick: () => navigateTo('schedules', 'all') },
          { label: 'Overdue', value: overdue.length, color: token.colorError, onClick: () => navigateTo('schedules', 'overdue') },
          { label: 'Due This Week', value: dueSoon.length, color: token.colorWarningText, onClick: () => navigateTo('schedules', 'due-soon') },
          { label: 'Open Work Orders', value: openWOs.length, color: token.colorPrimary, onClick: () => navigateTo('workorders', undefined, 'open') },
        ].map(({ label, value, color, onClick }) => (
          <Col flex="1 1 0" key={label}>
            <Card hoverable onClick={onClick} styles={{ body: { padding: '20px 24px' } }}>
              <Statistic title={`${label} →`} value={value} valueStyle={{ color }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title={<span><FlagOutlined /> Open WOs by Priority</span>}>
            {(['P1', 'P2', 'P3', 'P4'] as WorkOrder['priority'][]).map(p => (
              <div key={p} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Tag color={PRIORITY_COLORS[p]}>{PRIORITY_LABELS[p]}</Tag>
                <span style={{ fontWeight: 700 }}>{openWOs.filter(w => w.priority === p).length}</span>
              </div>
            ))}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<span><UnorderedListOutlined /> Next 5 Upcoming PM Tasks</span>} styles={{ body: { padding: 0 } }}>
            <Table<PMSchedule>
              rowKey="id"
              size="small"
              showHeader={false}
              pagination={false}
              dataSource={schedules.filter(s => s.status === 'active' && s.nextDueDate >= today()).slice(0, 5)}
              columns={[
                {
                  key: 'task',
                  render: (_, s) => (
                    <>
                      <div style={{ fontWeight: 600 }}>{s.taskName}</div>
                      <Typography.Text type="secondary" style={{ fontSize: 13 }}>{s.assetDescription}</Typography.Text>
                    </>
                  ),
                },
                { key: 'due', dataIndex: 'nextDueDate', align: 'right', width: 120 },
              ]}
              locale={{ emptyText: 'No upcoming tasks' }}
            />
          </Card>
        </Col>
      </Row>

      {/* PM Budget Estimate */}
      {activeSchedules.length > 0 && (
        <Card
          title={<span><DollarOutlined /> Estimated Annual PM Budget</span>}
          extra={<Typography.Text type="secondary" style={{ fontSize: 13 }}>frequency × hours × trade rates</Typography.Text>}
          style={{ marginTop: 16 }}
        >
          <Row gutter={16}>
            <Col flex="1 1 0">
              <Statistic
                title={<span><CalendarOutlined /> Est. annual PM cost</span>}
                value={totalAnnualPMCost} prefix="$" precision={0}
              />
            </Col>
            <Col flex="1 1 0">
              <Statistic
                title={<span><ClockCircleOutlined /> Per month (avg)</span>}
                value={totalMonthlyPMCost} prefix="$" precision={0}
              />
            </Col>
            <Col flex="1 1 0">
              <Statistic
                title={<span><UnorderedListOutlined /> Active PM tasks</span>}
                value={activeSchedules.length}
              />
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: 8 }}>Cost by trade</Typography.Text>
              {topTrades.map(([trade, cost]) => (
                <div key={trade} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 13 }}>
                  <span>{trade}</span><span style={{ fontWeight: 600 }}>${cost.toLocaleString('en-US', { maximumFractionDigits: 0 })}/yr</span>
                </div>
              ))}
            </Col>
            <Col xs={24} lg={12}>
              <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: 8 }}>Top 5 costliest tasks</Typography.Text>
              {[...scheduleCosts].sort((a, b) => b.annualCost - a.annualCost).slice(0, 5).map(c => (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 13, gap: 8 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.assetDescription}>{c.taskName}</span>
                  <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>${c.annualCost.toLocaleString('en-US', { maximumFractionDigits: 0 })}/yr</span>
                </div>
              ))}
            </Col>
          </Row>
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 12 }}>
            * Labor rates are estimates. Actual costs vary by contractor and region.
          </Typography.Text>
        </Card>
      )}
    </div>
  );

  const renderScheduleEditForm = () => (
    <div style={formGrid}>
      {field('Task Name', <Input value={editScheduleForm.taskName ?? ''} onChange={e => setEditScheduleForm((f: any) => ({ ...f, taskName: e.target.value }))} />)}
      {field('Asset Description', <Input value={editScheduleForm.assetDescription ?? ''} onChange={e => setEditScheduleForm((f: any) => ({ ...f, assetDescription: e.target.value }))} />)}
      {field('Location', <Input value={editScheduleForm.assetLocation ?? ''} onChange={e => setEditScheduleForm((f: any) => ({ ...f, assetLocation: e.target.value }))} />)}
      {field('Frequency', <Input type="number" value={editScheduleForm.frequencyValue ?? ''} onChange={e => setEditScheduleForm((f: any) => ({ ...f, frequencyValue: Number(e.target.value) }))} />)}
      {field('Unit', <Select style={{ width: '100%' }} value={editScheduleForm.frequencyUnit ?? 'months'} options={freqUnitOptions} onChange={v => setEditScheduleForm((f: any) => ({ ...f, frequencyUnit: v }))} />)}
      {field('Est. Hours', <Input type="number" value={editScheduleForm.estimatedHours ?? ''} onChange={e => setEditScheduleForm((f: any) => ({ ...f, estimatedHours: Number(e.target.value) }))} />)}
      {field('Next Due', <Input type="date" value={editScheduleForm.nextDueDate ?? ''} onChange={e => setEditScheduleForm((f: any) => ({ ...f, nextDueDate: e.target.value }))} />)}
      {field('Status', (
        <Select
          style={{ width: '100%' }} value={editScheduleForm.status ?? 'active'}
          options={[{ value: 'active', label: 'Active' }, { value: 'deferred', label: 'Deferred' }, { value: 'completed', label: 'Completed' }]}
          onChange={v => setEditScheduleForm((f: any) => ({ ...f, status: v }))}
        />
      ))}
      {field('Notes', <Input value={editScheduleForm.notes ?? ''} onChange={e => setEditScheduleForm((f: any) => ({ ...f, notes: e.target.value }))} />, true)}
    </div>
  );

  const scheduleColumns: ColumnsType<PMSchedule> = [
    {
      title: 'Asset / Task', key: 'task',
      render: (_, s) => (
        <>
          <div style={{ fontWeight: 600 }}>{s.taskName}</div>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>{s.assetDescription}{s.assetLocation ? ` · ${s.assetLocation}` : ''}</Typography.Text>
          {s.complianceRequired && s.complianceStandard && (
            <div style={{ marginTop: 4 }}><Tag>{s.complianceStandard}</Tag></div>
          )}
        </>
      ),
    },
    { title: 'Trade', dataIndex: 'trade', width: 130 },
    { title: 'Frequency', key: 'freq', width: 110, render: (_, s) => <span style={{ whiteSpace: 'nowrap' }}>{s.frequencyValue} {s.frequencyUnit}</span> },
    { title: 'Next Due', dataIndex: 'nextDueDate', width: 120, render: v => <span style={{ whiteSpace: 'nowrap' }}>{v}</span> },
    { title: 'Status', dataIndex: 'status', width: 100, render: (v: PMSchedule['status']) => <Tag color={SCHEDULE_STATUS_COLORS[v] ?? 'default'}>{v}</Tag> },
    {
      title: '', key: 'actions', width: 170, align: 'right',
      render: (_, s) => editingScheduleId === s.id ? (
        <Space size={4}>
          <Button type="primary" size="small" icon={<CheckOutlined />} title="Save" onClick={() => saveEditSchedule(s.id!)} />
          <Button size="small" icon={<CloseOutlined />} title="Cancel" onClick={() => setEditingScheduleId(null)} />
        </Space>
      ) : (
        <Space size={4}>
          <Popconfirm
            title={`Mark "${s.taskName}" as complete?`}
            description="The next due date will be recalculated."
            onConfirm={() => completeSchedule(s)}
          >
            <Button type="text" size="small" icon={<CheckOutlined />} title="Mark Complete" aria-label="Mark complete" />
          </Popconfirm>
          <Button type="text" size="small" icon={<EditOutlined />} title="Edit" aria-label="Edit schedule" onClick={() => startEditSchedule(s)} />
          <Button type="text" size="small" icon={<FileAddOutlined />} title="Create Work Order" aria-label="Create work order" onClick={() => createWOFromSchedule(s)} />
          <Popconfirm title="Delete this PM schedule?" onConfirm={() => deleteSchedule(s.id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} title="Delete" aria-label="Delete schedule" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderSchedules = () => (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Select
          style={{ minWidth: 190 }}
          value={scheduleFilter}
          onChange={v => setScheduleFilter(v)}
          options={[
            { value: 'all', label: `All (${schedules.length})` },
            { value: 'overdue', label: `Overdue (${overdue.length})` },
            { value: 'due-soon', label: `Due This Week (${dueSoon.length})` },
          ]}
        />
        <Space>
          <Button icon={<ThunderboltOutlined />} loading={seedBusy && !showSeed} onClick={openSeed}>
            Seed from Assessment
          </Button>
          <Button type="primary" icon={showScheduleForm ? <CloseOutlined /> : <PlusOutlined />} onClick={() => setShowScheduleForm(v => !v)}>
            {showScheduleForm ? 'Cancel' : 'Add Schedule'}
          </Button>
        </Space>
      </div>

      {showScheduleForm && (
        <Card title="New PM schedule" style={{ marginBottom: 16 }}>
          <div style={formGrid}>
            {field('Asset / Equipment Description *', <Input value={scheduleForm.assetDescription} onChange={e => setScheduleForm(f => ({ ...f, assetDescription: e.target.value }))} placeholder="e.g. AHU-1, Main Air Handler" />)}
            {field('Location', <Input value={scheduleForm.assetLocation} onChange={e => setScheduleForm(f => ({ ...f, assetLocation: e.target.value }))} placeholder="e.g. Mechanical Room 101" />)}
            {field('Task (from library)', (
              orgId && orgTasks.length === 0 ? (
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  No tasks in library — visit <b>Team &gt; PM Task Library</b> to set up your task library.
                </Typography.Text>
              ) : (
                <Select
                  style={{ width: '100%' }} showSearch optionFilterProp="label" allowClear
                  placeholder="— Select a task or enter custom below —"
                  value={scheduleForm.taskId || undefined}
                  options={taskLibraryOptions}
                  onChange={v => handleTaskSelect(v ?? '')}
                />
              )
            ), true)}
            {field('Task Name *', <Input value={scheduleForm.taskName} onChange={e => setScheduleForm(f => ({ ...f, taskName: e.target.value }))} placeholder="e.g. Air Filter Replacement" />)}
            {field('Trade', <Input value={scheduleForm.trade} onChange={e => setScheduleForm(f => ({ ...f, trade: e.target.value }))} placeholder="e.g. HVAC Tech" />)}
            {field('Frequency', <Input type="number" min={1} value={scheduleForm.frequencyValue} onChange={e => setScheduleForm(f => ({ ...f, frequencyValue: parseInt(e.target.value) || 1 }))} />)}
            {field('Unit', <Select style={{ width: '100%' }} value={scheduleForm.frequencyUnit} options={freqUnitOptions} onChange={v => setScheduleForm(f => ({ ...f, frequencyUnit: v as PMSchedule['frequencyUnit'] }))} />)}
            {field('Est. Hours', <Input type="number" min={0} step={0.5} value={scheduleForm.estimatedHours} onChange={e => setScheduleForm(f => ({ ...f, estimatedHours: parseFloat(e.target.value) || 0 }))} />)}
            {field('Next Due Date *', <Input type="date" value={scheduleForm.nextDueDate} onChange={e => setScheduleForm(f => ({ ...f, nextDueDate: e.target.value }))} />)}
            {field('Status', (
              <Select
                style={{ width: '100%' }} value={scheduleForm.status}
                options={[{ value: 'active', label: 'Active' }, { value: 'deferred', label: 'Deferred' }, { value: 'inactive', label: 'Inactive' }]}
                onChange={v => setScheduleForm(f => ({ ...f, status: v as PMSchedule['status'] }))}
              />
            ))}
            {field('Compliance Standard', <Input value={scheduleForm.complianceStandard} onChange={e => setScheduleForm(f => ({ ...f, complianceStandard: e.target.value }))} placeholder="e.g. NFPA 25" />)}
            {field('Notes', <Input.TextArea rows={2} value={scheduleForm.notes} onChange={e => setScheduleForm(f => ({ ...f, notes: e.target.value }))} />, true)}
          </div>
          {scheduleFormError && <Alert type="error" showIcon message={scheduleFormError} style={{ marginTop: 14 }} />}
          <Space style={{ marginTop: 16 }}>
            <Button type="primary" onClick={saveSchedule} loading={savingSchedule}>Save</Button>
            <Button onClick={() => { setShowScheduleForm(false); setScheduleForm(emptyScheduleForm()); setScheduleFormError(null); }}>Cancel</Button>
          </Space>
        </Card>
      )}

      <Card styles={{ body: { padding: 0 } }}>
        <Table<PMSchedule>
          rowKey="id"
          size="middle"
          columns={scheduleColumns}
          dataSource={filteredSchedules}
          pagination={false}
          onRow={s => ({ style: rowTint(s) })}
          expandable={{
            expandedRowKeys: editingScheduleId ? [editingScheduleId] : [],
            expandedRowRender: () => renderScheduleEditForm(),
            showExpandColumn: false,
          }}
          locale={{
            emptyText: scheduleFilter === 'all'
              ? 'No PM schedules yet. Add one above.'
              : `No ${scheduleFilter === 'overdue' ? 'overdue' : 'due-this-week'} schedules.`,
          }}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );

  const renderWorkOrders = () => (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Space>
          <Select
            style={{ minWidth: 150 }} value={woStatusFilter} onChange={setWOStatusFilter}
            options={[
              { value: 'all', label: 'All Statuses' }, { value: 'open', label: 'Open' },
              { value: 'in-progress', label: 'In Progress' }, { value: 'completed', label: 'Completed' },
              { value: 'deferred', label: 'Deferred' }, { value: 'cancelled', label: 'Cancelled' },
            ]}
          />
          <Select
            style={{ minWidth: 130 }} value={woTypeFilter} onChange={setWOTypeFilter}
            options={[
              { value: 'all', label: 'All Types' }, { value: 'PM', label: 'PM' }, { value: 'reactive', label: 'Reactive' },
            ]}
          />
        </Space>
        <Button type="primary" icon={showWOForm ? <CloseOutlined /> : <PlusOutlined />} onClick={() => setShowWOForm(v => !v)}>
          {showWOForm ? 'Cancel' : 'Add Reactive WO'}
        </Button>
      </div>

      {showWOForm && (
        <Card title="New reactive work order" style={{ marginBottom: 16 }}>
          <div style={formGrid}>
            {field('Title *', <Input value={woForm.title} onChange={e => setWOForm(f => ({ ...f, title: e.target.value }))} placeholder="Brief description of the issue" />, true)}
            {field('Asset / Equipment', <Input value={woForm.assetDescription} onChange={e => setWOForm(f => ({ ...f, assetDescription: e.target.value }))} />)}
            {field('Location', <Input value={woForm.assetLocation} onChange={e => setWOForm(f => ({ ...f, assetLocation: e.target.value }))} />)}
            {field('Trade *', <Input value={woForm.trade} onChange={e => setWOForm(f => ({ ...f, trade: e.target.value }))} placeholder="e.g. HVAC Tech" />)}
            {field('Priority', (
              <Select
                style={{ width: '100%' }} value={woForm.priority}
                options={(['P1', 'P2', 'P3', 'P4'] as WorkOrder['priority'][]).map(p => ({ value: p, label: PRIORITY_LABELS[p] }))}
                onChange={v => setWOForm(f => ({ ...f, priority: v as WorkOrder['priority'] }))}
              />
            ))}
            {field('Due Date', <Input type="date" value={woForm.dueDate} onChange={e => setWOForm(f => ({ ...f, dueDate: e.target.value }))} />)}
            {field('Assigned To', <Input value={woForm.assignedTo} onChange={e => setWOForm(f => ({ ...f, assignedTo: e.target.value }))} />)}
            {field('Description', <Input.TextArea rows={3} value={woForm.description} onChange={e => setWOForm(f => ({ ...f, description: e.target.value }))} />, true)}
            {field('Notes', <Input.TextArea rows={2} value={woForm.notes} onChange={e => setWOForm(f => ({ ...f, notes: e.target.value }))} />, true)}
          </div>
          {woFormError && <Alert type="error" showIcon message={woFormError} style={{ marginTop: 14 }} />}
          <Space style={{ marginTop: 16 }}>
            <Button type="primary" onClick={saveWO} loading={savingWO}>Save</Button>
            <Button onClick={() => { setShowWOForm(false); setWOForm(emptyWOForm()); setWOFormError(null); }}>Cancel</Button>
          </Space>
        </Card>
      )}

      {filteredWOs.length === 0 ? (
        <Card><Empty description="No work orders match the current filter." /></Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {filteredWOs.map(wo => (
            <Card
              key={wo.id}
              size="small"
              title={(
                <Space size={4} wrap>
                  <Tag color={PRIORITY_COLORS[wo.priority]} style={{ marginInlineEnd: 0 }}>{wo.priority}</Tag>
                  <Tag color={STATUS_COLORS[wo.status]} style={{ marginInlineEnd: 0 }}>{wo.status}</Tag>
                  <Tag style={{ marginInlineEnd: 0 }}>{wo.type}</Tag>
                </Space>
              )}
              extra={(
                <Space size={0}>
                  <Button type="text" size="small" icon={<EditOutlined />} title="Edit" aria-label="Edit work order" onClick={() => startEditWO(wo)} />
                  <Popconfirm title="Delete this work order?" onConfirm={() => deleteWO(wo.id)} okText="Delete" okButtonProps={{ danger: true }}>
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} title="Delete" aria-label="Delete work order" />
                  </Popconfirm>
                </Space>
              )}
            >
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{wo.title}</div>
              {wo.assetDescription && (
                <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
                  {wo.assetDescription}{wo.assetLocation ? ` · ${wo.assetLocation}` : ''}
                </Typography.Text>
              )}
              <div style={{ fontSize: 13 }}><Typography.Text type="secondary">Trade:</Typography.Text> {wo.trade}</div>
              {wo.dueDate && <div style={{ fontSize: 13 }}><Typography.Text type="secondary">Due:</Typography.Text> {wo.dueDate}</div>}
              {wo.assignedTo && <div style={{ fontSize: 13 }}><Typography.Text type="secondary">Assigned:</Typography.Text> {wo.assignedTo}</div>}
              {wo.description && (
                <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginTop: 8, marginBottom: 0 }}>{wo.description}</Typography.Paragraph>
              )}

              {completingWO === wo.id && (
                <div style={{ marginTop: 14, borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 14, display: 'grid', gap: 10 }}>
                  {field('Completed Date', <Input type="date" value={completeForm.completedDate} onChange={e => setCompleteForm(f => ({ ...f, completedDate: e.target.value }))} />)}
                  {field('Labor Hours', <Input type="number" min={0} step={0.5} value={completeForm.laborHours} onChange={e => setCompleteForm(f => ({ ...f, laborHours: e.target.value }))} />)}
                  {field('Resolution', <Input.TextArea rows={2} value={completeForm.resolution} onChange={e => setCompleteForm(f => ({ ...f, resolution: e.target.value }))} />)}
                  <Space>
                    <Button type="primary" size="small" onClick={() => submitComplete(wo.id)}>Save</Button>
                    <Button size="small" onClick={() => setCompletingWO(null)}>Cancel</Button>
                  </Space>
                </div>
              )}

              {editingWOId === wo.id && (
                <div style={{ marginTop: 14, borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 14, display: 'grid', gap: 10 }}>
                  {field('Title', <Input value={editWOForm.title ?? ''} onChange={e => setEditWOForm((f: any) => ({ ...f, title: e.target.value }))} />)}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {field('Status', (
                      <Select
                        style={{ width: '100%' }} value={editWOForm.status ?? 'open'}
                        options={[
                          { value: 'open', label: 'Open' }, { value: 'in-progress', label: 'In Progress' },
                          { value: 'completed', label: 'Completed' }, { value: 'deferred', label: 'Deferred' },
                        ]}
                        onChange={v => setEditWOForm((f: any) => ({ ...f, status: v }))}
                      />
                    ))}
                    {field('Priority', (
                      <Select
                        style={{ width: '100%' }} value={editWOForm.priority ?? 'P3'}
                        options={['P1', 'P2', 'P3', 'P4'].map(p => ({ value: p, label: p }))}
                        onChange={v => setEditWOForm((f: any) => ({ ...f, priority: v }))}
                      />
                    ))}
                    {field('Assigned To', <Input value={editWOForm.assignedTo ?? ''} onChange={e => setEditWOForm((f: any) => ({ ...f, assignedTo: e.target.value }))} />)}
                    {field('Due Date', <Input type="date" value={editWOForm.dueDate ?? ''} onChange={e => setEditWOForm((f: any) => ({ ...f, dueDate: e.target.value }))} />)}
                  </div>
                  {field('Notes', <Input.TextArea rows={2} value={editWOForm.description ?? ''} onChange={e => setEditWOForm((f: any) => ({ ...f, description: e.target.value }))} />)}
                  <Space>
                    <Button type="primary" size="small" onClick={() => saveEditWO(wo.id!)}>Save</Button>
                    <Button size="small" onClick={() => setEditingWOId(null)}>Cancel</Button>
                  </Space>
                </div>
              )}

              {wo.status !== 'completed' && wo.status !== 'cancelled' && completingWO !== wo.id && editingWOId !== wo.id && (
                <div style={{ marginTop: 14, borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 12, display: 'flex', gap: 8 }}>
                  {wo.status === 'open' && (
                    <Button block icon={<PlayCircleOutlined />} onClick={() => updateWorkOrder(wo.id!, { status: 'in-progress' }).then(load)}>Start</Button>
                  )}
                  <Button block type="primary" icon={<CheckCircleOutlined />} onClick={() => { setCompletingWO(wo.id); setCompleteForm({ completedDate: today(), resolution: '', laborHours: '' }); }}>
                    Mark Complete
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderCalendar = () => (
    <div>
      {/* Overdue banner */}
      {overdue.length > 0 && (
        <Alert
          type="error" showIcon style={{ marginBottom: 16 }}
          message={<span><b>{overdue.length} overdue</b> — navigate to past months to see overdue items, or visit <b>PM Schedules</b> to mark them complete.</span>}
        />
      )}

      {/* Month navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <Button icon={<LeftOutlined />} aria-label="Previous month" onClick={() => { setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1)); setSelectedDay(null); }} />
        <Typography.Title level={5} style={{ margin: 0 }}>{MONTH_NAMES[calendarDate.getMonth()]} {calendarDate.getFullYear()}</Typography.Title>
        <Button icon={<RightOutlined />} aria-label="Next month" onClick={() => { setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1)); setSelectedDay(null); }} />
      </div>

      {/* Calendar grid (custom layout, AntD-token styled) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
        {DOW_LABELS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 12, color: token.colorTextTertiary, fontWeight: 600, padding: '4px 0' }}>{d}</div>
        ))}
        {calendarGrid.map((day, i) => {
          if (!day) return <div key={i} style={{ minHeight: 90, background: token.colorFillQuaternary, borderRadius: token.borderRadiusLG }} />;
          const daySchedules = schedulesByDay[day] ?? [];
          const isToday = day === today();
          const isSelected = day === selectedDay;
          return (
            <div
              key={day} onClick={() => setSelectedDay(isSelected ? null : day)}
              style={{
                minHeight: 90, borderRadius: token.borderRadiusLG, padding: '5px 6px', cursor: 'pointer',
                border: `1px solid ${isSelected ? token.colorPrimary : token.colorBorderSecondary}`,
                background: isSelected ? token.colorPrimaryBg : token.colorBgContainer,
              }}
            >
              <div style={isToday
                ? { background: token.colorPrimary, color: '#fff', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 600 }
                : { fontSize: 11.5, fontWeight: 600, color: token.colorTextTertiary }}
              >
                {parseInt(day.slice(-2), 10)}
              </div>
              {daySchedules.slice(0, 3).map(s => (
                <Tag
                  key={s.id} color={scheduleChipColor(s)} title={`${s.assetDescription} — ${s.taskName}`}
                  style={{ display: 'block', fontSize: 10.5, lineHeight: '16px', paddingInline: 4, marginInlineEnd: 0, marginTop: 3, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {s.taskName}
                </Tag>
              ))}
              {daySchedules.length > 3 && <div style={{ fontSize: 10.5, color: token.colorTextTertiary, marginTop: 2 }}>+{daySchedules.length - 3} more</div>}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 14, marginTop: 10, justifyContent: 'flex-end' }}>
        {[[token.colorError, 'Overdue'], [token.colorWarning, 'Due Soon'], [token.colorSuccess, 'Scheduled']].map(([color, label]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: color, display: 'inline-block' }} />
            <span style={{ fontSize: 11.5, color: token.colorTextTertiary }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Selected day detail */}
      {selectedDay && (
        <Card
          size="small" style={{ marginTop: 14 }}
          title={parseLocalDate(selectedDay).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          extra={<Button type="text" size="small" icon={<CloseOutlined />} aria-label="Close day detail" onClick={() => setSelectedDay(null)} />}
        >
          {selectedDaySchedules.length === 0 ? (
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>No tasks scheduled for this day.</Typography.Text>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {selectedDaySchedules.map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <Tag color={scheduleChipColor(s)} style={{ whiteSpace: 'nowrap', marginTop: 2 }}>
                    {isOverdue(s.nextDueDate) ? 'Overdue' : isDueSoon(s.nextDueDate) ? 'Due Soon' : 'Scheduled'}
                  </Tag>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>{s.assetDescription}</div>
                    <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
                      {s.taskName} · {s.trade} · every {s.frequencyValue} {s.frequencyUnit}
                    </Typography.Text>
                    {s.assetLocation && <Typography.Text type="secondary" style={{ fontSize: 13 }}>{s.assetLocation}</Typography.Text>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );

  const tabItems = [
    { key: 'dashboard', label: <span><DashboardOutlined /> Dashboard</span>, children: renderDashboard() },
    {
      key: 'schedules',
      label: (
        <span>
          <CalendarOutlined /> PM Schedules
          {overdue.length > 0 && <Tag color="red" style={{ marginLeft: 8, marginInlineEnd: 0 }}>{overdue.length}</Tag>}
        </span>
      ),
      children: renderSchedules(),
    },
    {
      key: 'workorders',
      label: (
        <span>
          <FileDoneOutlined /> Work Orders
          {openWOs.length > 0 && <Tag color="green" style={{ marginLeft: 8, marginInlineEnd: 0 }}>{openWOs.length}</Tag>}
        </span>
      ),
      children: renderWorkOrders(),
    },
    {
      key: 'calendar',
      label: (
        <span>
          <CalendarOutlined /> Calendar
          {overdue.length > 0 && <Tag color="red" style={{ marginLeft: 8, marginInlineEnd: 0 }}>{overdue.length}</Tag>}
        </span>
      ),
      children: renderCalendar(),
    },
  ];

  return (
    <div>
      {/* Page head */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
        <Typography.Title level={4} style={{ margin: 0 }}><CalendarOutlined /> PM Scheduler</Typography.Title>
        <Typography.Text type="secondary">Preventive maintenance &amp; work orders</Typography.Text>
      </Card>

      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Tabs
        activeKey={activeTab}
        onChange={k => { setActiveTab(k as Tab); setSelectedDay(null); }}
        items={tabItems}
      />

      <Modal
        title="Seed PM schedules from facility assessment"
        open={showSeed}
        width={1000}
        okText={`Create ${seedSelected.length} schedule${seedSelected.length !== 1 ? 's' : ''}`}
        okButtonProps={{ disabled: seedSelected.length === 0, loading: seedBusy }}
        onOk={confirmSeed}
        onCancel={() => setShowSeed(false)}
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          Assessment assets are matched to the PM task library by description.
          First due dates are set one interval from today — adjust after creating if needed.
        </Typography.Paragraph>
        {seedAssessments.length > 1 && (
          <Select
            style={{ minWidth: 260, marginBottom: 12 }}
            value={seedAssessmentId ?? undefined}
            options={seedAssessments.map(a => ({ value: a.id!, label: a.buildingName || 'Untitled' }))}
            onChange={id => {
              const a = seedAssessments.find(x => x.id === id);
              if (a) selectSeedAssessment(a);
            }}
          />
        )}
        {seedUnmatched.length > 0 && (
          <Alert
            type="warning" showIcon style={{ marginBottom: 12 }}
            message={`${seedUnmatched.length} asset(s) had no matching PM tasks and were skipped`}
            description={<Typography.Text type="secondary" style={{ fontSize: 12.5 }}>{seedUnmatched.join(' · ')}</Typography.Text>}
          />
        )}
        <Table<SeedRow>
          dataSource={seedRows}
          rowKey="key"
          size="small"
          pagination={{ pageSize: 8, showTotal: (t, r) => `${r[0]}–${r[1]} of ${t}` }}
          rowSelection={{ selectedRowKeys: seedSelected, onChange: setSeedSelected }}
          columns={[
            {
              title: 'Asset', dataIndex: 'assetDescription', ellipsis: true,
              render: (v, r) => (
                <Space size={6}>
                  <span>{v}</span>
                  {r.exists && <Tag color="gold">Already scheduled</Tag>}
                </Space>
              ),
            },
            { title: 'Location', dataIndex: 'assetLocation', width: 120, ellipsis: true },
            { title: 'Task', dataIndex: 'taskName', ellipsis: true },
            { title: 'Trade', dataIndex: 'trade', width: 110, ellipsis: true },
            {
              title: 'Frequency', key: 'freq', width: 100,
              render: (_, r) => `${r.frequencyValue} ${r.frequencyUnit}`,
            },
            { title: 'Hrs', dataIndex: 'estimatedHours', width: 56, align: 'right' },
            {
              title: '', key: 'comp', width: 110,
              render: (_, r) => r.complianceRequired ? <Tag color="blue">Compliance</Tag> : null,
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default PMSchedulerModule;
